import { useMemo } from "react";
import { IShopCategory, IShopFilters, IUserBalance, ShopSortOptionEnum } from "../../types";

import { useTranslation } from "react-i18next";
import Dropdown from "@/components/dropdown/Dropdown";
import SortIcon from "../../assets/imgs/sort.svg";
import styles from "../../ShopWidget.module.scss";

const ShopFilters = ({
  filters,
  setFilters,
  categories,
  tokens,
}: {
  filters: IShopFilters;
  setFilters: React.Dispatch<React.SetStateAction<IShopFilters>>;
  categories: IShopCategory[];
  tokens: IUserBalance["token"][];
}) => {
  const { i18n, t } = useTranslation();
  const lng = i18n.language;
  const allCategoriesLabel = "All Categories";

  const categoryFilters = useMemo(() => {
    const options = categories.reduce(
      (acc, category) => {
        const translatedLabel = (category?.translations as Record<string, string>)?.[lng];
        const label = (translatedLabel || category.name || "").trim();
        const value = category.id?.toString() ?? "";

        if (!label || label.toLowerCase() === allCategoriesLabel.toLowerCase()) {
          return acc;
        }

        if (!acc.some((option) => option.value === value || option.label === label)) {
          acc.push({ label, value });
        }

        return acc;
      },
      [{ label: allCategoriesLabel, value: "" }]
    );

    return options;
  }, [categories, lng]);

  const selectedCategory =
    categoryFilters.find((category) => category.value === (filters.categoryId?.toString() ?? "")) ??
    categoryFilters[0];

  const sortOptions = useMemo(
    () => [
      { label: t("shop.dateNewToOld"), value: ShopSortOptionEnum.DATE_DESC },
      { label: t("shop.dateOldToNew"), value: ShopSortOptionEnum.DATE_ASC },
      { label: t("shop.priceHighToLow"), value: ShopSortOptionEnum.PRICE_DESC },
      { label: t("shop.priceLowToHigh"), value: ShopSortOptionEnum.PRICE_ASC },
      { label: t("shop.canPurchase"), value: ShopSortOptionEnum.CAN_PURCHASE },
    ],
    [t]
  );

  const selectedSort =
    sortOptions.find((option) => option.value === filters.sortBy) ?? sortOptions[0];

  const tokenFilters = useMemo(() => {
    const uniqueTokens = tokens.reduce<IUserBalance["token"][]>((acc, token) => {
      const tokenId = Number(token?.id);

      if (!tokenId || acc.some((item) => Number(item.id) === tokenId)) {
        return acc;
      }

      acc.push(token);
      return acc;
    }, []);

    return uniqueTokens;
  }, [tokens]);

  const shouldShowTokenFilters = tokenFilters.length > 1;

  return (
    <div className={styles.filtersContainer}>
      <Dropdown
        placeholderText={allCategoriesLabel}
        defaultValue={selectedCategory}
        options={categoryFilters}
        className={styles.categoryDropdown}
        onSelect={(option: { value: string }) =>
          setFilters((prev) => ({
            ...prev,
            categoryId: option.value ? Number(option.value) : null,
          }))
        }
      />
      <div className={styles.tokenAndSortFilters}>
        {shouldShowTokenFilters && (
          <div className={styles.tokenFilters} role="tablist" aria-label="Token filters">
            <button
              type="button"
              role="tab"
              aria-selected={filters.tokenId === null}
              className={`${styles.tokenFilterButton} ${
                filters.tokenId === null ? styles.tokenFilterButtonSelected : ""
              }`}
              onClick={() =>
                setFilters((prev) => ({
                  ...prev,
                  tokenId: null,
                }))
              }
            >
              All
            </button>
            {tokenFilters.map((token) => (
              <button
                key={token.id}
                type="button"
                role="tab"
                aria-label={token.name}
                aria-selected={filters.tokenId === Number(token.id)}
                className={`${styles.tokenFilterButton} ${
                  filters.tokenId === Number(token.id) ? styles.tokenFilterButtonSelected : ""
                }`}
                onClick={() =>
                  setFilters((prev) => ({
                    ...prev,
                    tokenId: Number(token.id),
                  }))
                }
              >
                <img src={token.image} alt="" className={styles.tokenFilterImg} />
              </button>
            ))}
          </div>
        )}
        <Dropdown
          placeholderText={t("shop.sortBy")}
          defaultValue={selectedSort}
          options={sortOptions}
          className={styles.sortFilterDropdown}
          buttonIcon={SortIcon}
          onSelect={(option: { value: string }) =>
            setFilters((prev) => ({
              ...prev,
              sortBy: option.value,
            }))
          }
        />
      </div>
    </div>
  );
};

export default ShopFilters;
